/**
 * EPBCS Phase 2 — budgeting grid for Other / Driver based forms.
 * Handles Manual, Increment %, Driver and Trend calculation methods live.
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, DownloadSimple, UploadSimple, CircleNotch } from "@phosphor-icons/react";
import { PovBar, useDims, defaultPov, dimOptions, fmtMonth, fmtNum, ExportButton } from "./povUtils";
import { ActionButton, sectionForForm } from "./WorkflowActions";
import { ItemComments, useCommentCounts } from "./ItemComments";

const ImportExportTools = ({ form, pov, onImported }) => {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const downloadTemplate = async () => {
    const qs = new URLSearchParams({ kind: "budget", form, ...pov }).toString();
    const r = await api.get(`/epbcs/upload/template?${qs}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `vaku_${form}_template.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };
  const importFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/epbcs/upload/budget", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.failed) {
        toast.warning(`${r.data.saved} rows imported, ${r.data.failed} rejected — ` +
          (r.data.errors?.[0] ? `row ${r.data.errors[0].row}: ${r.data.errors[0].error}` : ""));
      } else toast.success(`${r.data.saved} rows imported`);
      onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <span className="flex items-center gap-2">
      <button onClick={downloadTemplate} data-testid={`grid-template-btn-${form}`}
        title="Download an Excel template pre-filled for this form and POV"
        className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded border border-slate-300 text-slate-600 hover:border-[#0B1E3F] hover:text-[#0B1E3F]">
        <DownloadSimple size={11} weight="bold" /> Template
      </button>
      <button onClick={() => fileRef.current?.click()} disabled={busy}
        data-testid={`grid-import-btn-${form}`}
        title="Import completed template (.xlsx)"
        className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded border border-slate-300 text-slate-600 hover:border-[#0B1E3F] hover:text-[#0B1E3F] disabled:opacity-50">
        {busy ? <CircleNotch size={11} className="animate-spin" /> : <UploadSimple size={11} weight="bold" />}
        Import
      </button>
      <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
        data-testid={`grid-import-input-${form}`}
        onChange={(e) => importFile(e.target.files?.[0])} />
    </span>
  );
};

const BASELINE_LABELS = {
  prior_month: "Prior Month", cy_budget: "Current Year Budget",
  cy_actual: "Current Year Value", cy_avg: "Average of CY Value",
};
const POV_KEYS = ["entity", "division", "line_of_business", "department", "currency", "version"];

export default function BudgetGrid({ form, exportReport }) {
  const dims = useDims();
  const [pov, setPov] = useState(null);
  const [grid, setGrid] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const commentCounts = useCommentCounts(sectionForForm(form), pov);
  useEffect(() => { api.get("/epbcs/my-access").then((r) => setIsAdmin(r.data.role === "admin")); }, []);
  useEffect(() => { if (dims && !pov) setPov(defaultPov(dims, POV_KEYS)); }, [dims]); // eslint-disable-line

  const load = () => {
    if (!pov) return;
    setError(null);
    api.post("/epbcs/budget-grid", { form, pov })
      .then((r) => setGrid(r.data))
      .catch((e) => { setGrid(null); setError(e?.response?.data?.detail || e.message); });
  };
  useEffect(load, [pov, form]); // eslint-disable-line

  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid={`grid-error-${form}`}>{error}</p>;

  const canEdit = grid && (grid.perm === "input" || grid.perm === "full");

  const saveRow = async (row, patch = {}) => {
    const values = patch.values ?? row.values;
    const bad = Object.entries(values || {}).find(([, v]) =>
      v !== "" && v != null && (isNaN(+v) || +v < 0));
    if (row.method !== "calculated" && bad) {
      toast.error(`Value for ${fmtMonth(bad[0])} must be a positive number`);
      load();
      return;
    }
    setSaving(true);
    try {
      await api.post("/epbcs/budget-line", {
        form, pov, code: row.code, row_type: row.row_type,
        method: patch.method ?? row.method,
        baseline_fc: patch.baseline_fc ?? row.baseline_fc,
        baseline_bud: patch.baseline_bud ?? row.baseline_bud,
        increment_fc: patch.increment_fc ?? row.increment_fc,
        increment_bud: patch.increment_bud ?? row.increment_bud,
        values,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      load();
    } finally { setSaving(false); }
  };

  const updLocal = (code, upd) => setGrid((g) => ({
    ...g, rows: g.rows.map((r) => (r.code === code ? { ...r, ...upd } : r)),
  }));
  const updCell = (code, m, v) => setGrid((g) => ({
    ...g, rows: g.rows.map((r) => (r.code === code
      ? { ...r, values: { ...r.values, [m]: v } } : r)),
  }));

  const seedActuals = async () => {
    await api.post("/epbcs/actuals/seed-demo");
    load();
  };

  return (
    <div className="space-y-4" data-testid={`budget-grid-${form}`}>
      <PovBar testid={`pov-${form}`} pov={pov} setPov={setPov} fields={[
        { k: "entity", label: "Entity", options: dimOptions(dims, "entity") },
        { k: "division", label: "Division", options: dimOptions(dims, "division") },
        { k: "line_of_business", label: "Line of Business", options: dimOptions(dims, "line_of_business") },
        { k: "department", label: "Department", options: dimOptions(dims, "department") },
        { k: "currency", label: "Currency", options: dimOptions(dims, "currency") },
        { k: "version", label: "Version", options: dimOptions(dims, "version") },
      ]} extra={<ActionButton section={sectionForForm(form)} pov={pov} onChanged={load} />} />

      {!grid ? <p className="text-sm text-slate-400">Loading grid…</p> : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100">
            <span className="text-[11px] text-slate-500">
              Actuals through <b>{fmtMonth(grid.actuals_through)}</b> (grey) · open months are plannable
              {saving && <span className="ml-2 text-[#0B1E3F] font-semibold">Saving…</span>}
            </span>
            <span className="ml-auto flex items-center gap-2.5">
              {canEdit && <ImportExportTools form={form} pov={pov} onImported={load} />}
              {exportReport && <ExportButton report={exportReport} pov={pov} />}
              {!grid.has_actuals && isAdmin && (
                <button onClick={seedActuals} data-testid={`seed-actuals-${form}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100">
                  <Sparkle size={11} weight="fill" /> Seed demo actuals
                </button>
              )}
              {!canEdit && <span className="text-[10px] font-bold uppercase text-slate-400">Read-only ({grid.perm})</span>}
            </span>
          </div>
          <div className="relative overflow-auto max-h-[calc(100vh-300px)]">
            <table className="text-[12px] w-full border-collapse">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.12em] uppercase text-slate-600">
                  <th className="py-2.5 px-3 min-w-[200px] sticky left-0 top-0 bg-[#F1F5F9] z-30 border-b border-r border-slate-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">Account / Driver</th>
                  <th className="py-2.5 px-2 min-w-[110px] sticky top-0 bg-[#F1F5F9] z-20 border-b border-slate-200">Method</th>
                  <th className="py-2.5 px-2 min-w-[120px] sticky top-0 bg-[#F1F5F9] z-20 border-b border-slate-200">Baseline (Fcst)</th>
                  <th className="py-2.5 px-2 min-w-[70px] sticky top-0 bg-[#F1F5F9] z-20 border-b border-slate-200">Incr % (Fcst)</th>
                  <th className="py-2.5 px-2 min-w-[120px] sticky top-0 bg-[#F1F5F9] z-20 border-b border-slate-200">Baseline (Bud)</th>
                  <th className="py-2.5 px-2 min-w-[70px] sticky top-0 bg-[#F1F5F9] z-20 border-b border-slate-200">Incr % (Bud)</th>
                  {grid.months.map((m) => {
                    const sc = grid.scenario_by_month?.[m];
                    const scColor = sc === "Actual" ? "bg-slate-200 text-slate-600"
                      : sc === "Forecast" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
                    return (
                      <th key={m} className={`py-2 px-2 text-right min-w-[76px] whitespace-nowrap sticky top-0 bg-[#F1F5F9] z-20 border-b border-slate-200 ${m <= grid.actuals_through ? "text-slate-400" : ""}`}>
                        <div>{fmtMonth(m)}</div>
                        {sc && <span className={`inline-block mt-0.5 text-[8px] font-bold normal-case tracking-normal px-1 rounded ${scColor}`} data-testid={`scenario-badge-${form}-${m}`}>{sc}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((row) => (
                  <GridRow key={row.code} row={row} grid={grid} canEdit={canEdit}
                    pov={pov} commentCounts={commentCounts}
                    onLocal={updLocal} onCell={updCell} onSave={saveRow} form={form} />
                ))}
                {grid.rows.length === 0 && (
                  <tr><td colSpan={6 + grid.months.length} className="py-6 text-center text-slate-400">
                    No accounts configured for this form. Set the account&apos;s Budget Method in Setup → Dimension Masters.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const CalcCell = ({ row, form, canEdit, isCalc, field, options, onLocal, onSave, suffix }) => {
  if (row.derived || !isCalc) return <td className="py-1.5 px-2"><span className="text-slate-300">—</span></td>;
  if (options) {
    return (
      <td className="py-1.5 px-2">
        {canEdit ? (
          <select value={row[field]} data-testid={`${field}-${form}-${row.code}`}
            onChange={(e) => { onLocal(row.code, { [field]: e.target.value }); onSave(row, { [field]: e.target.value }); }}
            className="border border-slate-300 px-1 py-0.5 text-[11px] bg-white rounded">
            {options.map((b) => <option key={b} value={b}>{BASELINE_LABELS[b]}</option>)}
          </select>
        ) : <span className="text-[11px]">{BASELINE_LABELS[row[field]]}</span>}
      </td>
    );
  }
  return (
    <td className="py-1.5 px-2">
      {canEdit ? (
        <input type="number" value={row[field]} data-testid={`${field}-${form}-${row.code}`}
          onChange={(e) => onLocal(row.code, { [field]: e.target.value })}
          onBlur={(e) => onSave(row, { [field]: +e.target.value || 0 })}
          className="w-16 border border-slate-300 px-1 py-0.5 text-[11px] text-right rounded" />
      ) : <span className="text-[11px]">{row[field]}{suffix}</span>}
    </td>
  );
};

const GridRow = ({ row, grid, canEdit, onLocal, onCell, onSave, form, pov, commentCounts }) => {
  const isCalc = row.method === "calculated";
  const editableCells = canEdit && !row.derived && !isCalc;
  return (
    <tr className="border-b border-slate-100 odd:bg-white even:bg-[#F8FAFC] hover:bg-[#0B1E3F]/[0.03] transition-colors" data-testid={`grid-row-${form}-${row.code}`}>
      <td className="py-1.5 px-3 sticky left-0 bg-inherit z-10 border-r border-slate-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.06)]">
        <div className="font-semibold whitespace-nowrap flex items-center gap-1.5">
          {row.name}
          <ItemComments section={sectionForForm(form)} pov={pov} itemType="account"
            itemKey={`${form}:${row.code}`} itemLabel={`${row.code} — ${row.name}`}
            count={commentCounts.counts[`${form}:${row.code}`] || 0} onChanged={commentCounts.refresh} />
        </div>
        <div className="text-[10px] text-slate-400 font-mono">
          {row.code}
          {row.row_type === "driver" && <span className="ml-1 px-1 rounded bg-amber-50 text-amber-700 font-sans font-bold uppercase text-[9px]">driver{row.unit ? ` · ${row.unit}` : ""}</span>}
          {row.derived && <span className="ml-1 px-1 rounded bg-[#0B1E3F]/5 text-[#0B1E3F] font-sans font-bold text-[9px]">{row.formula}</span>}
        </div>
      </td>
      <td className="py-1.5 px-2">
        {row.derived ? (
          <span className="text-[10px] font-bold uppercase text-[#0B1E3F]">Driver formula</span>
        ) : canEdit ? (
          <select value={row.method} data-testid={`method-${form}-${row.code}`}
            onChange={(e) => { onLocal(row.code, { method: e.target.value }); onSave(row, { method: e.target.value }); }}
            className="border border-slate-300 px-1 py-0.5 text-[11px] bg-white rounded">
            <option value="manual">Manual</option>
            <option value="calculated">Calculated</option>
          </select>
        ) : <span className="text-[11px] capitalize">{row.method}</span>}
      </td>
      <CalcCell row={row} form={form} canEdit={canEdit} isCalc={isCalc}
        field="baseline_fc" options={grid.baselines_fc} onLocal={onLocal} onSave={onSave} />
      <CalcCell row={row} form={form} canEdit={canEdit} isCalc={isCalc}
        field="increment_fc" suffix="%" onLocal={onLocal} onSave={onSave} />
      <CalcCell row={row} form={form} canEdit={canEdit} isCalc={isCalc}
        field="baseline_bud" options={grid.baselines_bud} onLocal={onLocal} onSave={onSave} />
      <CalcCell row={row} form={form} canEdit={canEdit} isCalc={isCalc}
        field="increment_bud" suffix="%" onLocal={onLocal} onSave={onSave} />
      {grid.months.map((m) => {
        const isActual = m <= grid.actuals_through;
        const inCy = +m.slice(0, 4) === grid.cy_year;
        const v = row.values[m];
        if (isActual) {
          return <td key={m} data-testid={`ro-cell-${form}-${row.code}-${m}`} className="py-1.5 px-2 text-right text-slate-400 bg-slate-100/70 whitespace-nowrap font-tabular-nums">{fmtNum(v)}</td>;
        }
        const cellEditable = editableCells ||
          (canEdit && !row.derived && isCalc && inCy &&
            (row.baseline_fc === "cy_budget" || row.baseline_bud === "cy_budget"));
        if (cellEditable) {
          return (
            <td key={m} className="py-1 px-1">
              <input type="number" min="0" value={v ?? ""} data-testid={`cell-${form}-${row.code}-${m}`}
                onChange={(e) => onCell(row.code, m, e.target.value)}
                onBlur={() => onSave(row)}
                className="w-[72px] border border-transparent hover:border-slate-300 bg-transparent px-1 py-0.5 text-[12px] text-right rounded-sm font-tabular-nums focus:border-[#C8A24B] focus:bg-[#FFFBEB] focus:ring-1 focus:ring-[#C8A24B] outline-none transition-all" />
            </td>
          );
        }
        return (
          <td key={m} data-testid={`ro-cell-${form}-${row.code}-${m}`} className={`py-1.5 px-2 text-right whitespace-nowrap font-medium font-tabular-nums ${row.derived || isCalc ? "text-[#0B1E3F]" : "text-slate-700"}`}>
            {fmtNum(v)}
          </td>
        );
      })}
    </tr>
  );
};
